import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import {
  Box,
  Divider,
  FormControl,
  Grid,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { notificationSettingsKeys } from "../../../../../api/queryKeys";
import { operationStatus } from "../../../../../components/ActionStatus";
import CustomToolTip from "../../../../../components/CustomTooltip";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { IntegrationsMapping } from "../../../../stack/integrations/IntegrationsMapping";
import { field_IDs_mapping } from "../../fieldMapping";
import AddSlackChannelModal from "./AddSlackChannelModal";
import { SLACK_TOOL } from "./GeneralNotificationsPage";

export default function SlackChannelSelect({ settingsData, setFetchStatus }) {
  const colors = useColors();
  const currentChannels = useRef(settingsData.slack_channels);
  const [channels, setChannels] = useState(settingsData.slack_channels);
  const [open, setOpen] = useState(false);
  const [disabledMenu, setDisabledMenu] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { updateNotificationSetting } = useAPI();
  const queryClient = useQueryClient();

  const handleChange = (event) => {
    const newValue = event.target.value;
    if (newValue.includes("data-ignore")) return;
    const updatedChannels = [];
    channels.forEach((channel) => {
      const updatedChannel = { ...channel };
      updatedChannel.selected = newValue.includes(updatedChannel.name);
      updatedChannels.push({ ...updatedChannel });
    });
    setChannels(updatedChannels);
  };

  const noChangesInSelectedChannels = () => {
    const initialSelected = currentChannels.current
      .filter((channel) => channel.selected)
      .map((channel) => channel.name);
    const currentSelected = channels
      .filter((channel) => channel.selected)
      .map((channel) => channel.name);

    const initialSet = new Set(initialSelected);
    const currentSet = new Set(currentSelected);

    return (
      initialSet.size === currentSet.size &&
      [...initialSet].every((name) => currentSet.has(name))
    );
  };

  const onMenuClose = () => {
    setOpen(false);
    if (noChangesInSelectedChannels()) return;

    setFetchStatus(operationStatus.LOADING);
    setDisabledMenu(true);

    updateNotificationSetting(
      SLACK_TOOL,
      field_IDs_mapping.SLACK.SLACK_CHANNELS,
      channels
    )
      .then(() => {
        setFetchStatus(operationStatus.SUCCESS);
        currentChannels.current = channels;
        queryClient.invalidateQueries({
          queryKey: notificationSettingsKeys.all,
        });
      })
      .catch(() => {
        setChannels(currentChannels.current);
        setFetchStatus(operationStatus.ERROR);
      })
      .finally(() => {
        setDisabledMenu(false);
      });
  };

  const slackIcon = (
    <SvgToIcon IconLogo={IntegrationsMapping["slack"].logo} width="33px" />
  );

  const selectedChannelNames = channels
    .filter((channel) => channel.selected)
    .map((channel) => channel.name);

  return (
    <>
      <FormControl sx={{ width: "220px" }}>
        <Select
          multiple
          displayEmpty
          value={selectedChannelNames}
          disabled={disabledMenu}
          onOpen={() => setOpen(true)}
          onClose={onMenuClose}
          onChange={handleChange}
          renderValue={(selected) => {
            let text =
              selected.length === 0 ? "Select channels" : selected.join(", ");

            return (
              <Grid container alignItems="center" sx={{ flexWrap: "nowrap" }}>
                {slackIcon}
                <Typography
                  variant="caption"
                  color={colors.text_primary}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {text}
                </Typography>
              </Grid>
            );
          }}
          IconComponent={(props) =>
            open ? (
              <KeyboardArrowUpRoundedIcon {...props} />
            ) : (
              <KeyboardArrowDownRoundedIcon {...props} />
            )
          }
          sx={{
            border: `1px solid ${colors.divider2}`,
            borderRadius: "10px",
            height: "32px",
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "& .MuiSelect-select": {
              padding: "0px",
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                border: `1px solid ${colors.divider2}`,
                borderRadius: "8px",
                boxShadow: colors.dropdown_menu_shadow,
              },
            },
          }}
        >
          {channels.map((channel) =>
            channel.verified ? (
              <MenuItem
                key={channel.id}
                value={channel.name}
                disableRipple
                sx={{
                  padding: "5px",
                  "&.Mui-selected": {
                    backgroundColor: "transparent !important",
                  },
                }}
              >
                <ListItemText>
                  <Checkbox
                    checked={channel.selected}
                    disableRipple
                    sx={{
                      "& .MuiSvgIcon-root": {
                        fontSize: 18,
                      },
                    }}
                  />
                  <Typography variant="caption">{channel.name}</Typography>
                </ListItemText>
              </MenuItem>
            ) : (
              <CustomToolTip title="Channel is unreachable">
                <Box>
                  <MenuItem key={channel.id} value={channel.name} disabled>
                    <ListItemText>
                      <Typography variant="caption">{channel.name}</Typography>
                    </ListItemText>
                  </MenuItem>
                </Box>
              </CustomToolTip>
            )
          )}
          {channels.length > 0 ? <Divider /> : null}
          <MenuItem
            value="data-ignore"
            disableRipple
            onClick={() => setIsModalOpen(true)}
          >
            <ListItemText>
              <Box
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
                gap={1}
              >
                <Typography variant="body2">+</Typography>
                <Typography variant="caption">Add new...</Typography>
              </Box>
            </ListItemText>
          </MenuItem>
        </Select>
      </FormControl>
      <AddSlackChannelModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        channels={channels}
        setChannels={setChannels}
      />
    </>
  );
}
